import classNames from "classnames/bind";
import * as React from "react";
import { AuditContext, AuditContextValue, mergeLocations, trackEvent, TrackEventParamsGetter } from "~/common/audit";
import { Keys } from "~/common/enums";
import { Text } from "~/sharedComponents/layout/Text";
import { Marker } from "~/sharedComponents/userflow/utils/marker";
import { icon, Icon, IconNames, IconSize } from "../icons";
import { Qtip } from "../tooltip";
import * as styles from "./Button.less";
import { ButtonColorSchemes, ButtonFlag, ButtonVariants } from "./Enums";

const Component = React.Component;
const classNamesCtx = classNames.bind(styles);

function isPromise(promise: any): promise is JQuery.Promise<any, any, any> {
    return promise && promise.always;
}

export type ViewProps = {
    variant?: ButtonVariants;
    colorScheme?: ButtonColorSchemes | string;
    flags?: ButtonFlag | ButtonFlag[];
    text?: string;
    qTipOptions?: React.ComponentProps<typeof Qtip>;
    title?: string;
    tooltipContent?: React.ReactElement;
    showTooltip?: boolean;
    active?: boolean;
    iconName?: IconNames;
    /** Icon to be displayed at the end of the button after text/children (text prop or children required) */
    endIcon?: IconNames;
    iconSize?: IconSize;
};

export interface ButtonProps extends ViewProps, Marker {
    action?: (e: React.MouseEvent | React.KeyboardEvent) => void;
    isLoading?: boolean;
    isVisibleTooltip?: boolean;
    disabled?: boolean;
    autoFocus?: boolean;
    focusable?: boolean;
    auditEvent?: string | TrackEventParamsGetter;
    showLoadingOnAjaxActions?: boolean;
    className?: string;
    refCallback?: F1<React.RefObject<HTMLSpanElement>, void>;
    stopPropagation?: boolean;
    preventDefault?: boolean;
    tabIndex?: number;
    style?: React.CSSProperties;
    ["aria-describedby"]?: string;
    ["aria-label"]?: string;
}

type State = {
    isLoading: boolean;
};

export class Button extends Component<ButtonProps, State> {
    public static IsButton = true;
    public static Role = "button";
    public static defaultProps: Partial<ButtonProps> = {
        isLoading: false,
        focusable: true,
    };
    context!: AuditContextValue;
    state = { isLoading: false };
    protected viewProps: Partial<ViewProps> = {
        flags: [] as ButtonFlag[],
    };
    private _ref = React.createRef<HTMLSpanElement>();
    private _isDestroyed = false;
    private _canHandleKeyUp = false;

    _handleKeyDown = (event: any) => {
        this._canHandleKeyUp = true;
    };

    _handleKeyUp = (event: any) => {
        if (event.key === Keys.Enter && this._canHandleKeyUp) {
            this.onPress(event);
        }

        this._canHandleKeyUp = false;
    };

    componentWillUnmount() {
        this._isDestroyed = true;
    }

    componentDidMount() {
        if (this.props.refCallback) {
            this.props.refCallback(this._ref);
        }

        if (this.props.focusable && this.props.autoFocus) {
            this.focus();
        }
    }

    focus(options?: FocusOptions) {
        this._ref.current.focus(options);
    }

    render() {
        const { variant, colorScheme, flags, title, tooltipContent, text, showTooltip, iconName, endIcon, iconSize } =
            composeProps(this.props, this.viewProps);

        const {
            disabled,
            focusable,
            className,
            active,
            "data-testid": dataTestId,
            "aria-describedby": ariaDescribedby,
            "aria-label": ariaLabel,
        } = this.props;
        const isLoading = this.props.isLoading || this.state.isLoading;

        let tabIndex = this.props.tabIndex || 0;
        if (this.props.disabled) {
            tabIndex = -1;
        }

        const hasText = !!text || React.Children.count(this.props.children) > 0;
        const loadingWithoutLeftIcon = !iconName && isLoading;
        const renderButton = (
            <span
                data-testid={dataTestId}
                tabIndex={focusable ? tabIndex : null}
                className={classNamesCtx(
                    styles.button,
                    variant || ButtonVariants.Filled,
                    colorScheme || ButtonColorSchemes.Neutral,
                    ...flags,
                    className,
                    iconName && styles.icon_container,
                    {
                        loading: isLoading,
                        disabled,
                        isActive: active,
                    }
                )}
                onClick={this.onPress}
                onKeyDown={this._handleKeyDown}
                onKeyUp={this._handleKeyUp}
                title={!showTooltip ? title : null}
                style={this.props.style}
                ref={this._ref}
                role={Button.Role}
                aria-pressed={active}
                aria-label={text ? ariaLabel ?? null : title}
                aria-describedby={ariaDescribedby}
            >
                {(iconName || isLoading) && (
                    <Icon
                        className={classNamesCtx(
                            hasText && styles.icon_with_text,
                            loadingWithoutLeftIcon && styles.loading_indicator_center
                        )}
                        iconName={isLoading ? icon.spinner : iconName}
                        size={iconSize}
                    />
                )}
                {this.props.children}
                {text && (
                    <Text className={loadingWithoutLeftIcon && styles.hidden_text} weight="semibold" truncate={true}>
                        {text}
                    </Text>
                )}
                {endIcon && (hasText || this.props.children) && (
                    <Icon className={styles.end_icon_with_text} iconName={endIcon} size={iconSize} />
                )}
            </span>
        );

        let toolTipText = tooltipContent ?? title;
        if (showTooltip && !!toolTipText) {
            return (
                <Qtip isVisible={this.props.isVisibleTooltip} {...this.props.qTipOptions}>
                    {renderButton}
                    <div>{toolTipText}</div>
                </Qtip>
            );
        }

        return renderButton;
    }

    private onPress = (e: React.MouseEvent | React.KeyboardEvent) => {
        if (!this.props.action || this.props.disabled) {
            return;
        }

        if (this.state.isLoading || this.props.isLoading) {
            return;
        }
        if (this.props.stopPropagation) {
            e.stopPropagation();
        }
        if (this.props.preventDefault) {
            e.preventDefault();
        }

        const result = this.props.action(e);

        if (this.props.auditEvent) {
            if (typeof this.props.auditEvent === "function") {
                // the same code as in useTrackEvent
                const { eventName, location, ...data } = this.props.auditEvent();
                trackEvent(eventName, {
                    ...data,
                    location: location ? mergeLocations(this.context.location, location) : this.context.location,
                });
            } else {
                trackEvent(this.props.auditEvent, this.context);
            }
        }

        if (this.props.showLoadingOnAjaxActions && isPromise(result)) {
            this.setState({ isLoading: true });
            result.always(this.onAjaxCompleted);
        }
    };

    private onAjaxCompleted = () => {
        if (!this._isDestroyed && this.state.isLoading) {
            this.setState({ isLoading: false });
        }
    };
}

Button.contextType = AuditContext;

export const createButton = (name: string, props?: Partial<ViewProps>) => {
    return class CustomButton extends Button {
        static displayName = name;
        viewProps = composeProps(props, this.viewProps);
    };
};

const composeProps = (propsOverride?: Partial<ViewProps>, ...defaultProps: Partial<ViewProps>[]) => {
    return defaultProps.reduce(
        (result, fromDefault) => ({
            ...fromDefault,
            ...result,
            flags: [].concat(result.flags || []).concat(fromDefault.flags || []),
        }),
        propsOverride || {}
    );
};
